import React, { useEffect, useState } from 'react';
import { AlertTriangle, Mail, MailWarning } from 'lucide-react';
import { Spinner } from '../../ui';

/**
 * Contact-email safety rail, v2 styling: research found no address for this
 * supplier, so sending stays disabled until the buyer saves one here.
 */
interface MissingEmailBannerProps {
  threadId: string;
  onSave: (email: string) => Promise<boolean>;
  isSaving: boolean;
  error: string | null;
}

const MissingEmailBanner: React.FC<MissingEmailBannerProps> = ({ threadId, onSave, isSaving, error }) => {
  const [email, setEmail] = useState('');

  // A fresh thread starts with a fresh draft.
  useEffect(() => setEmail(''), [threadId]);

  const handleSave = async () => {
    if (!email.trim() || isSaving) return;
    const saved = await onSave(email);
    if (saved) setEmail('');
  };

  return (
    <div className="shrink-0 border-b border-warn-border bg-warn-bg px-[16px] py-[12px]">
      <div className="flex items-start gap-s3">
        <MailWarning className="mt-[2px] h-4 w-4 shrink-0 text-warn-ink" />
        <div className="min-w-0 flex-1">
          <p className="m-0 text-body-sm font-bold text-warn-ink">No contact email found for this supplier</p>
          <p className="m-0 mt-[2px] text-meta text-ink-secondary">
            Research didn't surface an address. Add one to enable sending — it saves to this supplier.
          </p>
          <div className="mt-s3 flex max-w-md items-center gap-s2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
              }}
              placeholder="sales@supplier.com"
              disabled={isSaving}
              className="w-full flex-1 rounded-control border border-warn-border bg-surface px-s4 py-[7px] font-sans text-body-sm text-ink outline-none placeholder:text-ink-muted focus:border-warn-ink disabled:opacity-60"
            />
            <button
              type="button"
              onClick={handleSave}
              disabled={!email.trim() || isSaving}
              className="flex shrink-0 items-center gap-s2 rounded-control border border-warn-ink bg-warn-ink px-s4 py-[7px] font-sans text-body-sm font-semibold text-white disabled:opacity-40"
            >
              {isSaving ? <Spinner tone="white" size="sm" /> : <Mail className="h-3 w-3" />}
              {isSaving ? 'Saving…' : 'Save email'}
            </button>
          </div>
          {error && (
            <p className="m-0 mt-s2 flex items-center gap-s1 text-meta font-semibold text-danger-ink">
              <AlertTriangle className="h-3 w-3 shrink-0" /> {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MissingEmailBanner;
